import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskedLines, Reveal, RevealImage } from "@/components/motion";
import CTASection from "@/components/CTASection";
import { IMAGES } from "@/data/content";

const PILLARS = [
  {
    n: "01",
    title: "How I build your training",
    text: "My Bachelor of Exercise and Sport Science taught me how the body actually adapts to training: how muscle, bone, tendon and the nervous system respond to load, and how recovery makes it stick. When I write your program, I'm thinking about your training history, your current capacity, your schedule and your stress, not a template. That's why your program updates every 4-6 weeks. You change, and your training should change with you.",
    items: ["Built around your body and your life", "Progression you can feel and measure", "Fatigue managed, not ignored", "Adjusted as you change"],
  },
  {
    n: "02",
    title: "How I teach technique",
    text: "Technique isn't about chasing a perfect lift you saw online. It's about finding the positions and patterns your body works best in. I watch how you move as a whole person, then give you one clear focus at a time instead of a list of everything at once. Video feedback means you can see what I see, so the next rep makes sense instead of feeling like guesswork.",
    items: ["Movement looked at as a whole", "One clear focus at a time", "Video feedback you can see", "Confidence built rep by rep"],
  },
  {
    n: "03",
    title: "How I help you understand your body",
    text: "So much confusion around food comes from not knowing what your body is actually doing. I'll teach you the physiology of hunger, how female hormones and metabolism affect your training, and how to fuel performance properly, including why restriction keeps backfiring. The goal is that you can read your own body and make good decisions long after our sessions end.",
    items: ["The physiology of hunger, explained", "Hormones, metabolism and training", "Fuelling for performance", "Why restriction backfires"],
  },
];

const Science = () => {

  return (
    <main data-testid="science-page">
      {/* HEADER */}
      <section className="bg-mist grain">
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 pt-32 md:pt-44 pb-16 md:pb-24">
          <p className="label-text text-ocean" data-testid="science-eyebrow">
            The Strength Movement philosophy · 01
          </p>
          <h1
            className="mt-6 font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.92] tracking-tight"
            data-testid="science-heading"
          >
            <MaskedLines lines={["The Science"]} />
            <span className="block overflow-hidden pb-[0.1em]">
              <motion.span
                className="block italic font-light text-ocean text-3xl sm:text-4xl lg:text-6xl"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                Understanding how your body works.
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-ink/70"
          >
            Everything I coach is grounded in my Bachelor of Exercise and Sport
            Science, but you'll never need a textbook to follow it. My job is
            to turn the evidence into training that makes sense for your body
            and your life, and to explain it well enough that you start
            trusting your own judgement.
          </motion.p>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-white" data-testid="science-pillars-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="border-t border-ink/10">
            {PILLARS.map((p, i) => (
              <Reveal key={p.n} delay={0.05 * i}>
                <div
                  className="grid gap-8 border-b border-ink/10 py-14 md:grid-cols-12 md:py-20"
                  data-testid={`science-pillar-${p.n}`}
                >
                  <div className="md:col-span-4">
                    <span className="font-display text-6xl italic font-light text-ocean/40">{p.n}</span>
                    <h2 className="mt-4 font-display text-3xl md:text-4xl leading-tight">{p.title}</h2>
                  </div>
                  <div className="md:col-span-5">
                    <p className="text-base md:text-lg leading-relaxed text-ink/70">{p.text}</p>
                  </div>
                  <div className="md:col-span-3">
                    <ul className="space-y-3">
                      {p.items.map((item) => (
                        <li key={item} className="flex items-center gap-3 text-sm text-ink/70">
                          <span className="h-1 w-1 rounded-full bg-ocean" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IMAGE + QUOTE BAND */}
      <section className="bg-ink text-white grain" data-testid="science-quote-section">
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-balance">
                  The science gives you clarity.
                  <span className="italic font-light text-white/60"> The psychology helps you live it.</span>
                </p>
                <Link
                  to="/psychology"
                  data-testid="science-to-psychology-link"
                  className="group mt-8 inline-flex items-center gap-2 text-sm font-bold text-white transition-colors hover:text-ocean"
                >
                  Continue to The Psychology
                  <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <RevealImage
                src={IMAGES.hero}
                alt="Strength training under the bar at The Strength Movement"
                className="arch-clip aspect-[4/5]"
                testId="science-image"
              />
            </div>
          </div>
        </div>
      </section>

      <CTASection
        title="Want coaching built on"
        accent="evidence, not trends?"
        body="Book a free 30-minute chat with Kim and see what evidence-based coaching actually feels like."
      />
    </main>
  );
};

export default Science;
